import type { ActivationGroup } from '@/features/activationGroup/types'
import type { ExtendedBid } from '@/features/bidding/types/bid'
import { getDraftOrOfferedBidsMatchingActivationGroups } from '@/features/bidding/utils/getDraftOrOfferedBidsMatchingActivationGroups'

/**
 * Determines if the Create Bid button should be disabled on the Volumes page.
 *
 * The button is disabled if:
 * - There are draft bids matching activation groups, but their count does not match the number of activation groups.
 * - There are no draft bids, and the number of latest draft or offered bids does not match the number of activation groups.
 *
 * @param latestDraftOrOfferedBids - The list of latest draft or offered bids.
 * @param eligibleActivationGroups - The list of eligible LIVE activation groups for bidding.
 * @returns True if the button should be disabled, false otherwise.
 */
export function isCreateBidButtonDisabledInVolumesPage(
  latestDraftOrOfferedBids: ExtendedBid[],
  eligibleActivationGroups: ActivationGroup[],
): boolean {
  if (eligibleActivationGroups.length === 0) return true

  const draftBidsMatchingActivationGroups = getDraftOrOfferedBidsMatchingActivationGroups(
    latestDraftOrOfferedBids,
    eligibleActivationGroups,
  )

  if (draftBidsMatchingActivationGroups.length > 0) {
    return draftBidsMatchingActivationGroups.length !== eligibleActivationGroups.length
  }

  // If bids are not in DRAFT, all the LIVE activation groups should be part of the offered bids
  return latestDraftOrOfferedBids.length !== eligibleActivationGroups.length
}
